import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import '../styles/EditModal.css';

function EditClientModal({ client, onClose, onClientUpdated, autoConnect }) {
  const [formData, setFormData] = useState({
    client_name: client.name,
    brand_color_primary: client.brand_color_primary || '#EC6433',
    assigned_com_id: client.assigned_com_id,
    client_email: client.client_email || '',
    report_schedule: client.report_schedule || 'quarterly',
    next_report_date: client.next_report_date || '',
    instagram_url: client.instagram_url || '',
    facebook_url: client.facebook_url || ''
  });
  const [tokenExpiry, setTokenExpiry] = useState(client.token_expires_at || null);
  const [tokenType, setTokenType] = useState(client.token_type || 'user');
  const [submitting, setSubmitting] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [savingPage, setSavingPage] = useState(false);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [exchanging, setExchanging] = useState(false);
  const [coms, setComs] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [showPageModal, setShowPageModal] = useState(false);
  const [takenPages, setTakenPages] = useState({});
  const [allPagesTaken, setAllPagesTaken] = useState(false);
  const [oauthLongLivedToken, setOauthLongLivedToken] = useState(null);

  // Auto-trigger OAuth if opened from the "Select Facebook Page" banner button
  React.useEffect(() => {
    if (autoConnect) {
      handleOAuthConnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect]);

  // Fetch COMs and user role when modal opens
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        // Get user role
        const authRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/check`, {
          withCredentials: true
        });
        setUserRole(authRes.data.user?.role);

        // If admin, fetch USERS
        if (authRes.data.user?.role === 'admin' || authRes.data.user?.role === 'superadmin') {
          const comsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`, {
            withCredentials: true
          });
          setComs(comsRes.data.users || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleOAuthConnect = () => {
    setOauthConnecting(true);

    const apiUrl = process.env.REACT_APP_API_URL;
    const popup = window.open(
      `${apiUrl}/api/auth/meta`,
      'MetaOAuth',
      'width=600,height=700,scrollbars=yes'
    );

    const handleMessage = async (event) => {
      if (event.data?.type === 'META_OAUTH_SUCCESS') {
        window.removeEventListener('message', handleMessage);
        setOauthConnecting(false);

        const { token, expires_at, token_type, pages } = event.data;

        // Save token to this client
        try {
          await axios.post(
            `${apiUrl}/api/clients/${client.id}/save-oauth-token`,
            { access_token: token, expires_at, token_type },
            { withCredentials: true }
          );
          setTokenExpiry(expires_at);
          setTokenType(token_type || 'user');
          setOauthLongLivedToken(token);
        } catch (err) {
          toast.error('Failed to save token');
          return;
        }

        if (pages && pages.length > 0) {
          setAvailablePages(pages);
          setSelectedPageId(pages[0].id);

          // Check if any returned pages are already connected to other clients
          try {
            const checkRes = await axios.post(
              `${apiUrl}/api/clients/check-pages`,
              { page_ids: pages.map(p => p.id), current_client_id: client.id },
              { withCredentials: true }
            );
            const taken = checkRes.data.taken || {};
            setTakenPages(taken);
            setAllPagesTaken(
              pages.length > 0 && pages.every(p => taken[p.id])
            );
            // Auto-select the first page not already assigned to another client
            const bestDefault = pages.find(p => !taken[p.id]);
            if (bestDefault) setSelectedPageId(bestDefault.id);
          } catch (err) {
            console.error('Could not check page ownership:', err);
            setTakenPages({});
            setAllPagesTaken(false);
          }

          setShowPageModal(true);
        } else {
          toast.success(expires_at ? `✅ Connected! Token valid until ${expires_at}` : '✅ Connected! Token never expires');
          onClientUpdated();
        }
      }

      if (event.data?.type === 'META_OAUTH_ERROR') {
        window.removeEventListener('message', handleMessage);
        setOauthConnecting(false);
        toast.error(`Connection failed: ${event.data.error}`);
      }
    };

    window.addEventListener('message', handleMessage);

    // Fallback: if popup closes without postMessage (user closed it manually)
    const pollClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollClosed);
        window.removeEventListener('message', handleMessage);
        setOauthConnecting(false);
      }
    }, 500);
  };

  // Facebook Login for Business — identical flow, just uses /api/auth/meta/business
  // which sends config_id instead of scope. Works for Business Suite / Business Manager clients.
  const handleFLBConnect = () => {
    setOauthConnecting(true);

    const apiUrl = process.env.REACT_APP_API_URL;
    const popup = window.open(
      `${apiUrl}/api/auth/meta/business`,
      'MetaFLBOAuth',
      'width=600,height=700,scrollbars=yes'
    );

    const handleMessage = async (event) => {
      if (event.data?.type === 'META_OAUTH_SUCCESS') {
        window.removeEventListener('message', handleMessage);
        setOauthConnecting(false);

        const { token, expires_at, token_type, pages } = event.data;

        try {
          await axios.post(
            `${apiUrl}/api/clients/${client.id}/save-oauth-token`,
            { access_token: token, expires_at, token_type },
            { withCredentials: true }
          );
          setTokenExpiry(expires_at);
          setTokenType(token_type || 'system_user');
          setOauthLongLivedToken(token);
        } catch (err) {
          toast.error('Failed to save token');
          return;
        }

        if (pages && pages.length > 0) {
          setAvailablePages(pages);
          setSelectedPageId(pages[0].id);

          try {
            const checkRes = await axios.post(
              `${apiUrl}/api/clients/check-pages`,
              { page_ids: pages.map(p => p.id), current_client_id: client.id },
              { withCredentials: true }
            );
            const taken = checkRes.data.taken || {};
            setTakenPages(taken);
            setAllPagesTaken(pages.length > 0 && pages.every(p => taken[p.id]));
            const bestDefault = pages.find(p => !taken[p.id]);
            if (bestDefault) setSelectedPageId(bestDefault.id);
          } catch (err) {
            setTakenPages({});
            setAllPagesTaken(false);
          }

          setShowPageModal(true);
        } else {
          toast.success(expires_at ? `✅ Connected! Token valid until ${expires_at}` : '✅ Connected! Token never expires');
          onClientUpdated();
        }
      }

      if (event.data?.type === 'META_OAUTH_ERROR') {
        window.removeEventListener('message', handleMessage);
        setOauthConnecting(false);
        toast.error(`Connection failed: ${event.data.error}`);
      }
    };

    window.addEventListener('message', handleMessage);

    const pollClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollClosed);
        window.removeEventListener('message', handleMessage);
        setOauthConnecting(false);
      }
    }, 500);
  };

  const handlePageSelect = async () => {
    if (!selectedPageId) return;
    const page = availablePages.find(p => p.id === selectedPageId);
    if (!page) return;

    setSavingPage(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/clients/${client.id}/select-page`,
        {
          page_id: page.id,
          page_token: page.page_token,
          instagram_id: page.instagram_id,
          all_pages: availablePages,
          access_token: oauthLongLivedToken
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        const igConnected = response.data.instagram_id ? ' + Instagram' : '';
        toast.success(`✅ Facebook Page${igConnected} connected!`);
        setAvailablePages([]);
        setSelectedPageId('');
        setShowPageModal(false);
        onClientUpdated();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save page selection');
    } finally {
      setSavingPage(false);
    }
  };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSubmitting(true);

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/clients/${client.id}`,
                formData,
                { withCredentials: true }
            );

            if (response.data.success) {
                toast.success(`✅ ${formData.client_name} updated!`);
                onClientUpdated();
                onClose();
            }
        } catch (err) {
            toast.error('Failed to update client');
            console.error('Error updating client:', err);
        } finally {
            setSubmitting(false);
        }
    };

  return (
    <>
    {/* Page Selection Modal — appears after OAuth completes */}
    {showPageModal && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: '#fff', borderRadius: '12px', padding: '32px',
          maxWidth: '480px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📘</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '22px', color: '#000' }}>One More Step!</h2>
            <p style={{ margin: 0, color: '#555', fontSize: '15px' }}>
              Meta connected successfully. Select which Facebook Page belongs to <strong>{client.name}</strong>.
            </p>
          </div>

          {allPagesTaken && (
            <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#1e40af' }}>
              ℹ️ All pages shown are already connected to your other clients — that's expected if you selected all your pages during OAuth. Pick the correct page for <strong>{client.name}</strong> and your other clients' tokens will refresh automatically.
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                Select the correct page:
              </label>
              <select
                value={selectedPageId}
                onChange={(e) => setSelectedPageId(e.target.value)}
                disabled={savingPage}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '6px',
                  border: '2px solid #3b82f6', fontSize: '15px', cursor: 'pointer'
                }}
              >
                {availablePages.map(page => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                    {takenPages[page.id] ? ` ⚠️ Already used by ${takenPages[page.id]}` : ''}
                    {page.instagram_id ? ' (+ Instagram)' : ' (Facebook only)'}
                  </option>
                ))}
              </select>
              {takenPages[selectedPageId] && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#dc2626', fontWeight: '600' }}>
                  ⚠️ This page is already connected to {takenPages[selectedPageId]} — are you sure this is correct?
                </div>
              )}
              {!takenPages[selectedPageId] && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: availablePages.find(p => p.id === selectedPageId)?.instagram_id ? '#16a34a' : '#ca8a04' }}>
                  {availablePages.find(p => p.id === selectedPageId)?.instagram_id
                    ? '✅ Instagram Business Account found — will be connected automatically'
                    : '⚠️ No Instagram Business Account linked to this page'}
                </div>
              )}
            </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="button"
              onClick={() => {
                setShowPageModal(false);
                toast('⚠️ Page not selected — complete setup from the client card', { icon: '🟡' });
              }}
              style={{
                flex: 1, padding: '12px', borderRadius: '6px',
                border: '1px solid #ddd', background: '#f9f9f9',
                cursor: 'pointer', fontSize: '14px', color: '#555'
              }}
            >
              Do Later
            </button>
            <button
              type="button"
              onClick={handlePageSelect}
              disabled={savingPage || !selectedPageId}
              style={{
                flex: 2, padding: '12px', borderRadius: '6px',
                border: 'none', background: '#16a34a',
                color: '#fff', cursor: 'pointer', fontSize: '15px',
                fontWeight: '700'
              }}
            >
              {savingPage ? '⏳ Saving...' : '✅ Confirm Page Selection'}
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal-content" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="edit-modal-header">
          <h2>✏️ Edit Client</h2>
          <button onClick={onClose} className="close-button">✕</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="edit-form">
          
          <div className="form-group">
            <label>Client Name *</label>
            <input
              type="text"
              name="client_name"
              value={formData.client_name}
              onChange={handleChange}
              required
              disabled={submitting}
              placeholder="e.g., Joe's Coffee Shop"
            />
          </div>

          <div className="form-group">
            <label>Brand Color (Primary)</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="color"
                name="brand_color_primary"
                value={formData.brand_color_primary}
                onChange={handleChange}
                disabled={submitting}
                style={{ width: '60px', height: '40px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                name="brand_color_primary"
                value={formData.brand_color_primary}
                onChange={handleChange}
                disabled={submitting}
                placeholder="#EC6433"
                style={{ flex: 1 }}
              />
            </div>
            <span className="field-hint">🎨 Used for charts and accents in reports</span>
          </div>

          {(userRole === 'admin' || userRole === 'superadmin') && (
            <div className="form-group">
              <label>Assigned User</label>
              <select
                name="assigned_com_id"
                value={formData.assigned_com_id}
                onChange={handleChange}
                disabled={submitting}
              >
                {coms.map(com => (
                  <option key={com.id} value={com.id}>
                    {com.name} ({com.email})
                  </option>
                ))}
              </select>
              <span className="field-hint">👤 Reassign this client to a different user</span>
            </div>
          )}

          {/* Report Scheduling Section */}
          <div className="form-section-divider">
            <h3>📧 Report Delivery</h3>
          </div>

          <div className="form-group">
            <label>Client Email</label>
            <input
              type="email"
              name="client_email"
              value={formData.client_email}
              onChange={handleChange}
              disabled={submitting}
              placeholder="e.g., owner@joescoffee.com"
            />
            <span className="field-hint">📬 Where automated reports will be sent</span>
          </div>

          <div className="form-group">
            <label>Report Schedule</label>
            <select
              name="report_schedule"
              value={formData.report_schedule}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="weekly">📅 Weekly (every 7 days)</option>
              <option value="biweekly">📅 Bi-Weekly (every 14 days)</option>
              <option value="monthly">📅 Monthly (every 30 days)</option>
              <option value="quarterly">📅 Quarterly (every 3 months)</option>
            </select>
            <span className="field-hint">⏰ How often reports are auto-generated and sent</span>
          </div>

          <div className="form-group">
            <label>Next Report Date</label>
            <input
              type="date"
              name="next_report_date"
              value={formData.next_report_date}
              onChange={handleChange}
              disabled={submitting}
            />
            <span className="field-hint">📆 The system will auto-generate a report on this date, then reschedule automatically</span>
          </div>

          {/* Social Profiles Section */}
          <div className="form-section-divider">
            <h3>🔗 Social Profiles</h3>
          </div>

          <div className="form-group">
            <label>Instagram URL</label>
            <input
              type="url"
              name="instagram_url"
              value={formData.instagram_url}
              onChange={handleChange}
              disabled={submitting}
              placeholder="https://instagram.com/yourclient"
            />
            <span className="field-hint">📷 Used for quick access from the client card</span>
          </div>

          <div className="form-group">
            <label>Facebook Page URL</label>
            <input
              type="url"
              name="facebook_url"
              value={formData.facebook_url}
              onChange={handleChange}
              disabled={submitting}
              placeholder="https://facebook.com/yourclientpage"
            />
            <span className="field-hint">📘 Used for quick access from the client card</span>
          </div>

          {/* Token Management Section */}
          <div className="form-section-divider">
            <h3>🔑 Meta API Connection</h3>
          </div>

          {/* Current token status */}
          {(tokenExpiry || tokenType === 'system_user') && (() => {
            // System user tokens (from Business Portfolio / FLB) never expire
            // Drive off token_type, not tokenExpiry — tokenExpiry can be 'never' incorrectly
            // if expires_in was missing from the Meta response
            if (tokenType === 'system_user') {
              return (
                <div className="token-status healthy">
                  🟢 Connected via Business Portfolio — token never expires
                </div>
              );
            }

            if (!tokenExpiry || tokenExpiry === 'never') {
              return (
                <div className="token-status healthy">
                  🟢 Connected — token valid
                </div>
              );
            }

            const today = new Date();
            const expiry = new Date(tokenExpiry);
            const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

            if (daysLeft <= 0) {
              return (
                <div className="token-status expired">
                  🔴 Token expired on {tokenExpiry} — reconnect below
                </div>
              );
            } else if (daysLeft <= 14) {
              return (
                <div className="token-status warning">
                  🟡 Token expires in {daysLeft} days ({tokenExpiry}) — consider reconnecting soon
                </div>
              );
            } else {
              return (
                <div className="token-status healthy">
                  🟢 Connected — token valid until {tokenExpiry} ({daysLeft} days remaining)
                </div>
              );
            }
          })()}

          {/* system_user tokens store NULL expiry — the block above already shows their status */}
          {!tokenExpiry && tokenType !== 'system_user' && (
            <div className="token-status none">
              ⚪ No Meta account connected yet
            </div>
          )}

          <button
            type="button"
            onClick={handleOAuthConnect}
            disabled={oauthConnecting}
            className="btn-token"
            style={{ marginTop: '12px' }}
          >
            {oauthConnecting ? '⏳ Connecting...' : tokenExpiry ? '🔄 Reconnect Meta Account' : '🔗 Connect Meta Account'}
          </button>
          <div style={{ marginTop: '6px' }}>
            <span className="field-hint">🔐 Opens a secure Meta login popup — connects Facebook + Instagram in one step</span>
          </div>
          <button
            type="button"
            onClick={handleFLBConnect}
            disabled={oauthConnecting}
            className="btn-token"
            style={{ marginTop: '8px', background: '#1877f2', borderColor: '#1877f2' }}
          >
            {oauthConnecting ? '⏳ Connecting...' : '🏢 Connect via Business Portfolio'}
          </button>
          <div style={{ marginTop: '6px' }}>
            <span className="field-hint">🏢 Use this if your client manages their pages through Facebook Business Suite or Business Manager</span>
          </div>

          {/* Superadmin only: Manual token fallback */}
          {userRole === 'superadmin' && (
          <div className="form-group" style={{ marginTop: '16px', padding: '16px', background: '#fff8f0', borderRadius: '8px', border: '1px solid #fcd9b6' }}>
            <label>🧪 Manual Token (Temporary Testing Only)</label>
            <input
              type="text"
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              disabled={exchanging}
              placeholder="Paste short-lived token from Graph API Explorer"
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', marginTop: '8px', boxSizing: 'border-box' }}
            />
            <button
              type="button"
              onClick={async () => {
                if (!manualToken.trim()) return;
                setExchanging(true);
                try {
                  const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/clients/${client.id}/exchange-token`,
                    { access_token: manualToken },
                    { withCredentials: true }
                  );
                  if (response.data.success) {
                    setTokenExpiry(response.data.expires_at);
                    setTokenType('user'); // pasted tokens are always 60-day user tokens
                    setManualToken('');
                    if (response.data.pages && response.data.pages.length > 0) {
                      setAvailablePages(response.data.pages);
                      setSelectedPageId(response.data.pages[0].id);
                      setShowPageModal(true);
                    } else {
                      toast.success(`✅ Token connected! Valid until ${response.data.expires_at}`);
                      onClientUpdated();
                    }
                  }
                } catch (err) {
                  toast.error(err.response?.data?.error || 'Failed to exchange token');
                } finally {
                  setExchanging(false);
                }
              }}
              disabled={exchanging || !manualToken.trim()}
              className="btn-token"
              style={{ marginTop: '10px' }}
            >
              {exchanging ? '⏳ Exchanging...' : '🔄 Exchange Token'}
            </button>
            <span className="field-hint" style={{ display: 'block', marginTop: '6px' }}>🔐 Superadmin emergency tool — use if OAuth is unavailable for a client</span>
          </div>
          )}
          {/* Page selection is now handled by the popup modal below */}

          {/* Actions */}
          <div className="edit-form-actions">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? '⏳ Saving...' : '💾 Save Changes'}
            </button>
          </div>

        </form>

      </div>
    </div>
    </>
  );
}

export default EditClientModal;